import React, { useEffect, useRef, useState, CSSProperties } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import { VariablePill } from '../../../App/InspectorDrawer/ConfigurationPanel/input-panels/helpers/inputs/VariablePillExtension';
import { VariableSuggestion } from '../../../App/InspectorDrawer/ConfigurationPanel/input-panels/helpers/inputs/VariableSuggestion';
import { convertMarkdownVariablesToHTML } from '../../../App/InspectorDrawer/ConfigurationPanel/input-panels/helpers/inputs/convertMarkdownToPills';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Link as LinkIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ButtonGroup, ButtonGroupSeparator } from '@/components/ui/button-group';
import 'tippy.js/dist/tippy.css';
import '../../../App/InspectorDrawer/ConfigurationPanel/input-panels/helpers/inputs/variable-suggestion.css';
import { convertEmailBuilderStylesToCSS } from './convertEmailBuilderStyles';

type InlineTextEditorProps = {
  initialContent: string;
  onSave: (markdown: string) => void;
  blockId: string;
  style?: any; // Email-builder-js style format
};

export default function InlineTextEditor({
  initialContent,
  onSave,
  blockId,
  style
}: InlineTextEditorProps) {
  const contentRef = useRef(initialContent);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Convert email-builder-js style format to React CSS properties
  const cssStyle = convertEmailBuilderStylesToCSS(style);

  // Track active states for toolbar buttons
  const [activeStates, setActiveStates] = useState({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    link: false,
  });

  const updateActiveStates = (editor: any) => {
    const newStates = {
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      strike: editor.isActive('strike'),
      link: editor.isActive('link'),
    };
    setActiveStates(newStates);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3], // Only h2 and h3 for regular text blocks
        },
        codeBlock: false,
        code: false,
        blockquote: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder: 'Type your text here...',
      }),
      VariablePill,
      VariableSuggestion.configure({
        isEmail: true,
      }),
      Markdown.configure({
        html: true,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: convertMarkdownVariablesToHTML(initialContent),
    editable: true,
    autofocus: 'end', // Auto-focus at end of text when editor mounts
    onUpdate: ({ editor }) => {
      // Store in ref but don't save to Zustand yet (avoid re-renders)
      const markdown = editor.storage.markdown?.getMarkdown() || editor.getText();
      contentRef.current = markdown;
      // Update toolbar button states
      updateActiveStates(editor);
    },
    onSelectionUpdate: ({ editor }) => {
      // Update toolbar when selection changes (e.g., cursor moves to bold text)
      updateActiveStates(editor);
    },
    onTransaction: ({ editor }) => {
      // Catch-all for any editor transaction
      updateActiveStates(editor);
    },
    onBlur: ({ editor }) => {
      // Save to Zustand only when user clicks away
      const markdown = editor.storage.markdown?.getMarkdown() || editor.getText();
      onSave(markdown);
    },
    editorProps: {
      attributes: {
        class: 'inline-tiptap-editor',
      },
    },
  });

  // Update content if initialContent changes (e.g., from undo/redo)
  useEffect(() => {
    if (editor && initialContent !== contentRef.current) {
      const htmlContent = convertMarkdownVariablesToHTML(initialContent);
      editor.commands.setContent(htmlContent);
      contentRef.current = initialContent;
    }
  }, [initialContent, editor]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        ...cssStyle, // Apply converted CSS styles to outer container
      }}
      ref={editorContainerRef}
    >
      <style>{`
        .inline-tiptap-editor {
          outline: none;
          width: 100%;
        }
        .inline-tiptap-editor p {
          margin: 0.5em 0;
        }
        .inline-tiptap-editor p:first-child {
          margin-top: 0;
        }
        .inline-tiptap-editor p:last-child {
          margin-bottom: 0;
        }
        .inline-tiptap-editor ul,
        .inline-tiptap-editor ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .inline-tiptap-editor a {
          color: hsl(var(--primary));
          text-decoration: underline;
        }
        .inline-tiptap-editor strong {
          font-weight: bold;
        }
        .inline-tiptap-editor em {
          font-style: italic;
        }
        .inline-tiptap-editor u {
          text-decoration: underline;
        }
        .inline-tiptap-editor s {
          text-decoration: line-through;
        }
        .inline-tiptap-editor .is-editor-empty:first-child::before {
          color: #999;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        /* Variable pill styles */
        .inline-tiptap-editor .variable-pill {
          color: white;
        }
        .inline-tiptap-editor .variable-pill:hover .pill-delete {
          display: inline-block !important;
        }
        .inline-tiptap-editor .variable-pill .pill-delete:hover {
          color: rgb(239, 68, 68);
        }
        /* Floating toolbar */
        .floating-toolbar {
          position: absolute;
          top: -48px;
          left: 0;
          z-index: 50;
          animation: fadeIn 0.15s ease-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Floating toolbar positioned above editor */}
      <div ref={toolbarRef} className="floating-toolbar">
        <ButtonGroup className="shadow-lg bg-background border border-border rounded-md">
          <Button
            size="sm"
            variant={activeStates.bold ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold className="h-4 w-4 text-foreground" />
          </Button>

          <Button
            size="sm"
            variant={activeStates.italic ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic className="h-4 w-4 text-foreground" />
          </Button>

          <Button
            size="sm"
            variant={activeStates.underline ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon className="h-4 w-4 text-foreground" />
          </Button>

          <Button
            size="sm"
            variant={activeStates.strike ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <Strikethrough className="h-4 w-4 text-foreground" />
          </Button>

          <ButtonGroupSeparator />

          <Button
            size="sm"
            variant={activeStates.link ? 'default' : 'ghost'}
            onClick={addLink}
          >
            <LinkIcon className="h-4 w-4 text-foreground" />
          </Button>
        </ButtonGroup>
      </div>

      {/* Editor content */}
      <EditorContent editor={editor} />
    </div>
  );
}
